import { useEffect, useMemo, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { mixedItems } from './data'
import { chunkBooksIntoShelves } from '../author-bookshelf/utils'

export function useMixedShelf() {
  const navigate = useNavigate()
  const { category } = useParams()

  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [viewMode, setViewMode] = useState('cover')
  const [sortOption, setSortOption] = useState('name')
  const [darkMode, setDarkMode] = useState(false)
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [booksPerShelf, setBooksPerShelf] = useState(12)
  const shelfContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setDarkMode(localStorage.getItem('darkMode') === 'true')
    const handleMouseMove = (e: MouseEvent) =>
      setMousePosition({ x: e.clientX, y: e.clientY })
    const calcShelf = () => {
      if (shelfContainerRef.current)
        setBooksPerShelf(
          Math.max(Math.floor(shelfContainerRef.current.offsetWidth / 60), 5),
        )
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('resize', calcShelf)
    calcShelf()
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('resize', calcShelf)
    }
  }, [])

  const filteredItems = useMemo(() => {
    return mixedItems.filter((item) => {
      const name = (item as any).title || (item as any).name || ''
      const matchesSearch = name
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
      if (category === 'books') return matchesSearch && item.itemType === 'book'
      if (category === 'merch')
        return matchesSearch && item.itemType === 'merch'
      return matchesSearch
    })
  }, [searchTerm, category])

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const nameA = ((a as any).title || (a as any).name || '').toLowerCase()
      const nameB = ((b as any).title || (b as any).name || '').toLowerCase()
      if (sortOption === 'releaseDate')
        return (
          new Date((b as any).releaseDate).getTime() -
          new Date((a as any).releaseDate).getTime()
        )
      return nameA.localeCompare(nameB)
    })
  }, [filteredItems, sortOption])

  const bookItems = useMemo(
    () => sortedItems.filter((i) => i.itemType === 'book'),
    [sortedItems],
  )
  const bookShelves = useMemo(
    () => chunkBooksIntoShelves(bookItems, booksPerShelf),
    [bookItems, booksPerShelf],
  )

  const navigateToCategory = (cat: string) => navigate(`/vendor/shelf/${cat}`)
  const navigateToEdit = (id: string, type: string) =>
    navigate(type === 'book' ? `/book/${id}/edit` : `/merch/${id}/edit`)

  return {
    category,
    searchTerm,
    setSearchTerm,
    showFilters,
    setShowFilters,
    viewMode,
    setViewMode,
    sortOption,
    setSortOption,
    darkMode,
    hoveredItemId,
    setHoveredItemId,
    mousePosition,
    booksPerShelf,
    shelfContainerRef,
    sortedItems,
    bookItems,
    bookShelves,
    navigateToCategory,
    navigateToEdit,
    navigate,
  }
}
